import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { PlusIcon } from 'lucide-react'
import { useMemo } from 'react'

import { Cell, DataTable, useDataTable } from '@/components/block/data-table'
import { Page } from '@/components/layout/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardExtendTable } from '@/components/ui/card-extend'

import { purchaseOrderApi } from '@/features/inventory/api/purchase-order-api'
import { PurchaseOrderDto } from '@/features/inventory/dto/purchase-order-dto'

import { useAuth } from '@/lib/auth'
import { toIdr } from '@/libs/utils/format'

export const Route = createFileRoute('/_app/inventory/po/')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <Page>
      <Page.Breadcrumbs />
      <PurchaseOrderTable />
    </Page>
  )
}

function PurchaseOrderTable() {
  const outletId = useAuth().outlet.oId

  const { data, isLoading } = useQuery({
    ...purchaseOrderApi.list.query({ params: { outletId } }),
  })

  const columns = useMemo(() => getColumns(), [])
  const table = useDataTable({
    columns,
    isLoading,
    data: data?.data.orders ?? [],
    state: {
      rowSelection: {},
    },
  })

  return (
    <CardExtendTable
      title='Daftar Purchase Order (PO)'
      action={
        <Button asChild>
          <Link from={Route.fullPath} to='/inventory/po/create'>
            <PlusIcon /> Tambah PO
          </Link>
        </Button>
      }
    >
      <DataTable.Root table={table}>
        <DataTable.Table />
        <DataTable.Pagination />
      </DataTable.Root>
    </CardExtendTable>
  )
}

function getColumns(): ColumnDef<PurchaseOrderDto>[] {
  return [
    {
      accessorKey: 'number',
      header: 'Nomor PO',
      cell: ({ row }) => {
        const { id, code } = row.original
        return (
          <Cell.Link from={Route.fullPath} to='/inventory/po/$id' params={{ id }}>
            {code}
          </Cell.Link>
        )
      },
    },
    {
      accessorKey: 'date',
      header: 'Tanggal',
      cell: ({ row }) => <Cell.Date date={row.original.date} />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const { status } = row.original
        return (
          <Cell>
            <Badge>{status}</Badge>
          </Cell>
        )
      },
    },
    {
      accessorKey: 'total',
      header: 'Total',
      cell: ({ row }) => <Cell.Text text={toIdr(row.original.total)} />,
    },
  ]
}
